// @ts-nocheck
import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import {
  Merge,
  MessageSquare,
  Users,
  Tag,
  Layers,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { supabase } from '@product-radar/shared';
import { useWorkspace } from '@/contexts/context';
import { toast } from 'sonner';

interface MergeIdea {
  id: string;
  title: string;
  status: string;
  priority: number;
  linkedFeedback: number;
  linkedClients: number;
  tags: string[];
}

interface MergeIdeasDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ideas: MergeIdea[];
  onSuccess: () => void;
}

export function MergeIdeasDialog({
  open,
  onOpenChange,
  ideas,
  onSuccess,
}: MergeIdeasDialogProps) {
  const { workspace, membership } = useWorkspace();
  const [primaryId, setPrimaryId] = useState<string>('');
  const [merging, setMerging] = useState(false);

  // Default to the idea with the most feedback, then highest priority
  useEffect(() => {
    if (open && ideas.length > 0) {
      const sorted = [...ideas].sort((a, b) => {
        if (b.linkedFeedback !== a.linkedFeedback) return b.linkedFeedback - a.linkedFeedback;
        if (b.priority !== a.priority) return b.priority - a.priority;
        return 0;
      });
      setPrimaryId(sorted[0].id);
    }
  }, [open, ideas]);

  const sourceIdeas = useMemo(
    () => ideas.filter((i) => i.id !== primaryId),
    [ideas, primaryId]
  );

  const summary = useMemo(() => {
    const totalFeedback = sourceIdeas.reduce((sum, i) => sum + i.linkedFeedback, 0);
    const totalClients = sourceIdeas.reduce((sum, i) => sum + i.linkedClients, 0);
    const totalTags = new Set(sourceIdeas.flatMap((i) => i.tags)).size;
    return { totalFeedback, totalClients, totalTags };
  }, [sourceIdeas]);

  const handleMerge = async () => {
    if (!workspace || !primaryId) return;

    setMerging(true);
    try {
      const sourceIds = sourceIdeas.map((i) => i.id);

      const { data, error } = await supabase.rpc('merge_ideas', {
        p_workspace_id: workspace.id,
        p_target_idea_id: primaryId,
        p_source_idea_ids: sourceIds,
        p_merged_by: membership?.user_id || null,
      });

      if (error) throw error;

      const result = data as any;
      toast.success(
        `Merged ${result.merged_count} idea${result.merged_count > 1 ? 's' : ''} successfully`,
        {
          description: `Transferred ${result.transferred_feedback} feedback, ${result.transferred_clients} clients, ${result.transferred_tags} tags`,
        }
      );

      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      console.error('Error merging ideas:', error);
      toast.error('Failed to merge ideas', {
        description: error.message,
      });
    } finally {
      setMerging(false);
    }
  };

  const getPriorityLabel = (p: number) => {
    const labels: Record<number, string> = {
      1: 'Lowest',
      2: 'Low',
      3: 'Medium',
      4: 'High',
      5: 'Critical',
    };
    return labels[p] || String(p);
  };

  const getPriorityColor = (p: number) => {
    const colors: Record<number, string> = {
      1: 'bg-gray-400',
      2: 'bg-blue-500',
      3: 'bg-yellow-500',
      4: 'bg-orange-500',
      5: 'bg-red-500',
    };
    return colors[p] || 'bg-gray-400';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Merge className="h-5 w-5" />
            Merge Ideas
          </DialogTitle>
          <DialogDescription>
            Select a primary idea. All feedback, clients, tags, and product mappings from the
            other ideas will be consolidated into it. The merged ideas will be archived.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Primary idea selection */}
          <div>
            <Label className="text-sm font-medium mb-3 block">
              Choose the primary idea (survives the merge)
            </Label>
            <RadioGroup value={primaryId} onValueChange={setPrimaryId}>
              <div className="space-y-2">
                {ideas.map((idea) => (
                  <label
                    key={idea.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      primaryId === idea.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <RadioGroupItem value={idea.id} className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm truncate">{idea.title}</span>
                        {primaryId === idea.id && (
                          <Badge variant="default" className="text-[10px] px-1.5 py-0">
                            Primary
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-[10px]">{idea.status}</Badge>
                        <span className="flex items-center gap-1">
                          <span className={`w-1.5 h-1.5 rounded-full ${getPriorityColor(idea.priority)}`} />
                          {getPriorityLabel(idea.priority)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          {idea.linkedFeedback}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {idea.linkedClients}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </RadioGroup>
          </div>

          <Separator />

          {/* Summary */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Merge summary
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>
                <span className="font-medium text-foreground">{sourceIdeas.length}</span>{' '}
                idea{sourceIdeas.length !== 1 ? 's' : ''} will be merged into{' '}
                <span className="font-medium text-foreground">
                  {ideas.find((i) => i.id === primaryId)?.title || 'the primary idea'}
                </span>
              </p>
              <div className="flex flex-wrap gap-3 pt-1">
                {summary.totalFeedback > 0 && (
                  <span className="flex items-center gap-1">
                    <MessageSquare className="h-3.5 w-3.5" />
                    {summary.totalFeedback} feedback
                  </span>
                )}
                {summary.totalClients > 0 && (
                  <span className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    {summary.totalClients} clients
                  </span>
                )}
                {summary.totalTags > 0 && (
                  <span className="flex items-center gap-1">
                    <Tag className="h-3.5 w-3.5" />
                    {summary.totalTags} tags
                  </span>
                )}
              </div>
              <p className="text-xs pt-1">
                Merged ideas will be set to "Merged" status and their descriptions appended to the primary.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={merging}>
            Cancel
          </Button>
          <Button onClick={handleMerge} disabled={merging || !primaryId || sourceIdeas.length === 0}>
            {merging ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Merging...
              </>
            ) : (
              <>
                <Merge className="h-4 w-4 mr-2" />
                Merge {sourceIdeas.length} idea{sourceIdeas.length !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
